import React, { useState } from 'react'
import { Button } from 'antd'
import Actions from 'actions'
import { useDispatch, useSelector } from 'react-redux'
import { routes } from 'consts'
import $t from '../../../../../i18n'
import Dialog from '../../../../common/Dialog'
import { isAssignedPcp } from 'utils/providerSearchUtils'
import dayjs from 'dayjs'
import { useNativeMessage } from 'hooks'

import { event, eventTypes } from 'consts/eventConsts'

const ProviderAction = ({
  isFirstAvailableProvider,
  provider,
  isPcpChangeFlow,
  className,
  customActionName,
  ...others
}) => {
  const dispatch = useDispatch()

  // Use native message hook for WebView communication
  const { sendMessage, isWebView, isWebViewAuthenticated } = useNativeMessage()

  const { telehealthPcp, physicalPcp, isUpdatePcpLoading } = useSelector(
    state => state.providers
  )
  const selectedMember = useSelector(state => state.account.selectedMember)
  const userDetails = useSelector(state => state.profile?.userDetails)

  const isProviderAntidote = provider?.is_antidote
  const pcpType = isProviderAntidote ? 'telehealth' : 'physical'

  // In WebView, always use PCP data from providers state (comes from native message)
  // In web, use userDetails if admin, providers state if not admin
  const isNotAdmin =
    !userDetails ||
    selectedMember?.id !== userDetails?.id ||
    isWebViewAuthenticated
  const telehealthPcpAccordingToUser = isNotAdmin
    ? telehealthPcp // From native message (WebView) or providers state (web, family member)
    : userDetails?.telehealthPcp // From userDetails (web, own account)

  const physicalPcpAccordingToUser = isNotAdmin
    ? physicalPcp // From native message (WebView) or providers state (web, family member)
    : userDetails?.physicalPcp // From userDetails (web, own account)

  const [isContactDetailsVisible, setIsContactDetailsVisible] = useState(false)
  const [isPcpUpdateAlertVisible, setIsPcpUpdateAlertVisible] = useState(false)
  const [loaderProviderId, setLoaderProviderId] = useState(null)

  const navigateToGetCareWithDesignatedProvider = () => {
    // if we are in webview, we need to send a message to the native app
    if (isWebView) {
      sendMessage('schedule-now')
    } else {
      dispatch(Actions.createAction(Actions.SCHEDULE_PCP))
      dispatch(Actions.createAction(Actions.NAVIGATED_TO, routes.GET_CARE))
    }
  }

  const navigateToGetCareWithoutDesignatedProvider = () => {
    dispatch(Actions.createAction(Actions.SCHEDULE_ANY_PCP))
    dispatch(Actions.createAction(Actions.NAVIGATED_TO, routes.GET_CARE))
  }

  const onSelectPcp = () => {
    // in case there is no previous physical pcp, update without popup approval
    const payload = {
      message: !!physicalPcpAccordingToUser?.length
        ? 'select-pcp'
        : 'select-pcp-no-approval',
      providerId: provider?.id,
      pcpType
    }
    // if we are in webview, we need to send a message to the native app
    if (isWebView) {
      sendMessage(payload)
    } else {
      // in case there is no previous physical pcp, update without popup approval
      !!physicalPcpAccordingToUser?.length
        ? setIsPcpUpdateAlertVisible(true)
        : onSelectPcpApproved()
    }
  }

  const onSelectPcpApproved = () => {
    const payload = {
      providerId: provider?.id,
      pcpType,
      patientId: selectedMember ? selectedMember?.id : userDetails?.id
    }
    dispatch(Actions.createAction(Actions.UPDATE_PCP_REQUESTED, payload))
    setLoaderProviderId(provider?.id)
    setIsPcpUpdateAlertVisible(false)
  }

  const actionName = customActionName
    ? customActionName
    : isPcpChangeFlow
    ? $t('profile.careTeam.select')
    : isAssignedPcp(provider) || isFirstAvailableProvider
    ? $t('profile.careTeam.scheduleNow')
    : $t('profile.careTeam.call')

  const onActionClick = () => {
    if (isFirstAvailableProvider)
      return navigateToGetCareWithoutDesignatedProvider()
    else if (isPcpChangeFlow) return onSelectPcp()
    else if (isProviderAntidote)
      return navigateToGetCareWithDesignatedProvider()
    else return setIsContactDetailsVisible(true)
  }

  const getAreYouSureText = () => {
    // get the family member name if selected
    const familyMemberSelectedName =
      !!selectedMember && selectedMember?.id !== userDetails?.id
        ? selectedMember?.firstName
        : null

    const nextMonthDate = dayjs()
      .add(1, 'month')
      .startOf('month')
      .format('MMMM D, YYYY')

    return isProviderAntidote
      ? familyMemberSelectedName
        ? $t('profile.careTeam.changePcpAlert.telehealthFamily', {
            name: familyMemberSelectedName
          })
        : $t('profile.careTeam.changePcpAlert.telehealth')
      : familyMemberSelectedName
      ? $t('profile.careTeam.changePcpAlert.physicalFamily', {
          name: familyMemberSelectedName,
          date: nextMonthDate
        })
      : $t('profile.careTeam.changePcpAlert.physical', {
          date: nextMonthDate
        })
  }

  // get pcp without the endDate (we can't edit pcp with end date)
  const newestPCP = physicalPcpAccordingToUser?.find(pcp => !pcp?.end_date)

  // disable the change button only in change pcp flow, according to -
  // 1. if the provider is not accepting new patients(accept_new_patients === false)
  // 2. OR if the provider is already the designated provider(telehealth or physical)
  // note - the provider can be null, is so we don't need to disable. just in case its false
  const isChangePcpDisabled =
    isPcpChangeFlow &&
    (provider?.accept_new_patients === false ||
      (isProviderAntidote
        ? telehealthPcpAccordingToUser?.id === provider?.id
        : newestPCP?.id === provider?.id))

  return (
    <>
      <Button
        type='outline'
        loading={isUpdatePcpLoading && loaderProviderId === provider?.id}
        disabled={
          (isUpdatePcpLoading && loaderProviderId !== provider?.id) ||
          isChangePcpDisabled
        }
        className={'provider-action trackable ' + className}
        data-name={'Provider Action - ' + actionName}
        onClick={e => {
          e?.stopPropagation()
          onActionClick()
          dispatch(
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.click,
              eventName: event.clickEvent.click,
              element: 'Provider Action - ' + actionName
            })
          )
        }}
        {...others}
      >
        {actionName}
      </Button>
      <Dialog
        title={$t('profile.careTeam.contactDetails') + ':'}
        isVisible={isContactDetailsVisible}
        footerActions={[
          <Button
            type='primary'
            onClick={() => setIsContactDetailsVisible(false)}
            key='1'
          >
            {$t('common.close')}
          </Button>
        ]}
        onCancel={() => setIsContactDetailsVisible(false)}
        className='care-team-details-dialog'
      >
        <div className='provider-details'>
          <div className='provider-details-row semi-bold'>
            {provider?.address}
          </div>
          <div className='provider-details-row'>
            {[provider?.city, provider?.state, provider?.zipcode]
              .filter(val => !!val)
              ?.join(', ')}
          </div>
          <div className='provider-details-row'>
            {provider?.provider_numbers?.[0]?.number}
          </div>
        </div>
      </Dialog>
      <Dialog
        title={$t('profile.careTeam.changePcpAlert.title') + ':'}
        isVisible={isPcpUpdateAlertVisible}
        footerActions={[
          <Button type='primary' onClick={onSelectPcpApproved} key='1'>
            {$t('profile.careTeam.changePcpAlert.select')}
          </Button>,
          <Button key='2' onClick={() => setIsPcpUpdateAlertVisible(false)}>
            {$t('common.no')}
          </Button>
        ]}
        onCancel={() => setIsPcpUpdateAlertVisible(false)}
      >
        <div className='pcp-update-alert'>{getAreYouSureText()}</div>
      </Dialog>
    </>
  )
}

export default ProviderAction
